import { Button, Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, useFieldArray } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { ZodObject, ZodRawShape } from 'zod';
import { ApiError } from '../../../api';
import { ShowFieldErrors } from '../../../utils/forms';
import { useSafeParseFormField } from '../../hooks/safe-parse-form-field';
import { FileUploader } from '../FileUploader';
import { FormFeedback } from '../FormFeedback';
import { FormFieldVisibility, FormLabel } from '../FormLabel';
import { FORM_NAME, MAX_SIZE, ACCEPT } from './constants';

interface InsuranceFormGroupProps {
  control: Control;
  errors: FieldErrors;
  serverErrors: ApiError | undefined;
  showServerFieldErrors: ShowFieldErrors | undefined;
  validationSchema: ZodObject<ZodRawShape>;
}

export const InsuranceFormGroup = ({
  control,
  errors,
  serverErrors,
  showServerFieldErrors,
  validationSchema,
}: InsuranceFormGroupProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAME, // unique name for your Field Array
  });
  const isParsed = useSafeParseFormField(control, FORM_NAME, validationSchema);

  const onAppendClick = () => {
    append({
      file: null,
    });
  };

  const onRemoveClick = (index: number) => {
    remove(index);
  };

  return (
    <Form.Group className="mb-3">
      <FormLabel visibility={FormFieldVisibility.CONNECTED}>Insurance Certificates</FormLabel>
      <ul className="list-unstyled mb-3">
        {fields.map((item, index) => (
          <li key={item.id}>
            <Row className="justify-content-end mb-4 mt-2">
              <Col>
                <FileUploader
                  accept={ACCEPT}
                  maxSize={MAX_SIZE}
                  name={`${FORM_NAME}.${index}.file`}
                  disabledPreview={true}
                  control={control}
                  dest="providers_documents"
                  isClearable={false}
                  isInvalid={
                    !!errors[FORM_NAME]?.[index]?.file || !!showServerFieldErrors?.[index]?.['file']
                  }
                />
                <FormFeedback
                  clientError={errors[FORM_NAME]?.[index]?.file}
                  serverErrors={serverErrors?.body[FORM_NAME]?.[index]?.file}
                />
              </Col>
              <Col xs="auto">
                <Button
                  className="text-danger"
                  type="button"
                  variant="link"
                  size="lg"
                  onClick={() => onRemoveClick(index)}
                >
                  <BsTrash />
                </Button>
              </Col>
            </Row>
          </li>
        ))}
      </ul>
      <Button
        className="d-flex align-items-center"
        variant="outline-primary"
        type="button"
        disabled={!!fields.length && !isParsed}
        onClick={onAppendClick}
      >
        <BsPlus className="me-1" /> Add Insurance
      </Button>
    </Form.Group>
  );
};
